// src/views/AppsPage.jsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import PublicTopBar from "./PublicTopBar";
import Footer from "../components/Footer";
import { publicSiteWebService } from "../services/publicSiteWebService";

const AUDIENCES = [
  {
    key: "parents",
    icon: "ti-user-heart",
    titre: "Pour les parents",
    intro: "Suivez la journée de votre enfant depuis votre téléphone.",
    points: [
      "Alerte à l'arrivée, au retard, à l'absence et à la sortie",
      "Notes, bulletins et rang dès leur publication",
      "Justification d'absence avec certificat en pièce jointe",
      "Messagerie avec les enseignants et l'administration",
      "Situation financière et reçus de chaque enfant",
    ],
  },
  {
    key: "ecoles",
    icon: "ti-school",
    titre: "Pour les écoles",
    intro: "Pointez les élèves et informez les familles sans papier.",
    points: [
      "Pointage des entrées et sorties avec photo",
      "Statut présent, retard ou absent calculé automatiquement",
      "Saisie des notes par les enseignants",
      "Envoi des notifications et des notes d'information",
      "Suivi des justifications d'absence",
    ],
  },
];

const STEPS = [
  { icon: "ti-download", titre: "Installez l'application", texte: "Depuis le Play Store, l'App Store ou le fichier APK." },
  { icon: "ti-login", titre: "Connectez-vous", texte: "Utilisez l'identifiant remis par l'école de votre enfant." },
  { icon: "ti-bell-ringing", titre: "Activez les notifications", texte: "Autorisez-les sur votre téléphone pour ne rien manquer." },
];

export default function AppsPage() {
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useState({});
  const [contact, setContact] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const data = await publicSiteWebService.getPublicData();
        setParams(data?.parametres || {});
        setContact(data?.contact || {});
      } catch (e) {
        console.error("Erreur chargement page apps:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const nomPlateforme = params.nom_plateforme || "Suivi Scolaire";
  const email = contact.email || "info@enumera.africa";

  const stores = [
    { key: "play", icon: "ti-brand-android", sub: "Disponible sur", main: "Google Play", url: params.url_play_store },
    { key: "apple", icon: "ti-brand-apple", sub: "Disponible sur", main: "App Store", url: params.url_app_store },
    { key: "apk", icon: "ti-download", sub: "Téléchargement direct", main: "Fichier APK", url: params.url_apk },
  ];
  const aucunLien = stores.every((s) => !s.url);

  if (loading) {
    return (
      <div className="apps-page">
        <PublicTopBar />
        <div className="apps-loader"><div className="apps-spinner" /></div>
        <Footer />
        <style>{CSS}</style>
      </div>
    );
  }

  return (
    <div className="apps-page">
      <style>{CSS}</style>
      <PublicTopBar />

      <section className="apps-hero">
        <div className="apps-wrap">
          <div className="apps-head">
            <div className="apps-badge"><i className="ti ti-device-mobile" /> Applications mobiles</div>
            <h1 className="apps-title">{nomPlateforme} dans votre poche</h1>
            <p className="apps-lead">
              Une application pour les parents, une pour les écoles. Les informations
              arrivent en temps réel, où que vous soyez.
            </p>
            <div className="apps-stores">
              {stores.map((s) =>
                s.url ? (
                  <a key={s.key} href={s.url} target="_blank" rel="noopener noreferrer" className="apps-store">
                    <i className={`ti ${s.icon}`} />
                    <span><small>{s.sub}</small><strong>{s.main}</strong></span>
                  </a>
                ) : (
                  <span key={s.key} className="apps-store apps-store-off" aria-disabled="true">
                    <i className={`ti ${s.icon}`} />
                    <span><small>Bientôt</small><strong>{s.main}</strong></span>
                  </span>
                )
              )}
            </div>
            {aucunLien && (
              <p className="apps-note">
                Les liens de téléchargement arrivent bientôt. Pour être prévenu,
                écrivez-nous à <a href={`mailto:${email}`}>{email}</a>.
              </p>
            )}
          </div>

          <div className="apps-audiences">
            {AUDIENCES.map((a) => (
              <article key={a.key} className={`apps-card apps-card-${a.key}`}>
                <div className="apps-card-icon"><i className={`ti ${a.icon}`} /></div>
                <h2>{a.titre}</h2>
                <p>{a.intro}</p>
                <ul>
                  {a.points.map((p) => (
                    <li key={p}><i className="ti ti-check" /> {p}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>

          <div className="apps-steps-block">
            <h2>Pour commencer</h2>
            <ol className="apps-steps">
              {STEPS.map((s, i) => (
                <li key={s.titre}>
                  <span className="apps-step-num">{i + 1}</span>
                  <div>
                    <h3><i className={`ti ${s.icon}`} /> {s.titre}</h3>
                    <p>{s.texte}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className="apps-actions">
            <Link to="/" className="apps-cta"><i className="ti ti-arrow-left" /> Retour à l'accueil</Link>
            <Link to="/help" className="apps-cta-secondary"><i className="ti ti-help-circle" /> Centre d'aide</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const CSS = `
  .apps-page { --n-bg:#f8f9fa; --n-card:#fff; --n-ink:#0f1b2d; --n-mut:#64748b; --n-b:#e8e8ee;
    --pr:#004080; --pr-lt:#0066CC; --pr-bg:#e6eef7; --or:#C08A2E; --or-bg:#fbf3e3;
    min-height:100vh; display:flex; flex-direction:column; background:var(--n-bg); }
  .apps-hero { flex:1; padding:120px 20px 48px; }
  .apps-wrap { max-width:960px; margin:0 auto; }
  .apps-head { text-align:center; margin-bottom:40px; }
  .apps-badge { display:inline-flex; align-items:center; gap:8px; background:var(--pr-bg); color:var(--pr);
    padding:6px 16px; border-radius:20px; font-size:13px; font-weight:600; margin-bottom:16px; }
  .apps-title { font-size:38px; font-weight:700; color:var(--n-ink); margin:0 0 12px; line-height:1.2; }
  .apps-lead { font-size:16px; line-height:1.7; color:var(--n-mut); max-width:600px; margin:0 auto 24px; }
  .apps-stores { display:flex; justify-content:center; gap:12px; flex-wrap:wrap; }
  .apps-store { display:flex; align-items:center; gap:12px; padding:10px 20px; border-radius:12px;
    background:var(--pr); color:#fff; text-decoration:none; transition:background .2s, transform .2s; }
  .apps-store:hover { background:var(--pr-lt); color:#fff; transform:translateY(-2px); }
  .apps-store i { font-size:28px; }
  .apps-store span { display:flex; flex-direction:column; text-align:left; }
  .apps-store small { font-size:10px; opacity:.75; }
  .apps-store strong { font-size:15px; font-weight:600; }
  .apps-store-off { background:#e2e8f0; color:var(--n-mut); cursor:not-allowed; }
  .apps-store-off:hover { background:#e2e8f0; color:var(--n-mut); transform:none; }
  .apps-note { margin:16px 0 0; font-size:14px; color:var(--n-mut); }
  .apps-note a { color:var(--pr); font-weight:600; }

  .apps-audiences { display:grid; grid-template-columns:1fr 1fr; gap:20px; margin-bottom:40px; }
  .apps-card { background:var(--n-card); border:1px solid var(--n-b); border-radius:16px; padding:28px; }
  .apps-card-icon { width:48px; height:48px; border-radius:12px; display:flex; align-items:center; justify-content:center; margin-bottom:14px; }
  .apps-card-icon i { font-size:26px; }
  .apps-card-parents .apps-card-icon { background:var(--pr-bg); color:var(--pr); }
  .apps-card-ecoles .apps-card-icon { background:var(--or-bg); color:var(--or); }
  .apps-card h2 { font-size:20px; margin:0 0 6px; color:var(--n-ink); }
  .apps-card > p { font-size:14px; color:var(--n-mut); margin:0 0 16px; line-height:1.6; }
  .apps-card ul { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:10px; }
  .apps-card li { display:flex; gap:10px; font-size:14px; line-height:1.5; color:var(--n-ink); }
  .apps-card li i { color:var(--pr); flex-shrink:0; margin-top:2px; }
  .apps-card-ecoles li i { color:var(--or); }

  .apps-steps-block { background:var(--n-card); border:1px solid var(--n-b); border-radius:16px; padding:28px 32px; margin-bottom:32px; }
  .apps-steps-block h2 { font-size:20px; margin:0 0 20px; color:var(--n-ink); }
  .apps-steps { list-style:none; margin:0; padding:0; display:grid; grid-template-columns:repeat(3,1fr); gap:24px; }
  .apps-steps li { display:flex; gap:14px; align-items:flex-start; }
  .apps-step-num { width:32px; height:32px; border-radius:50%; background:var(--pr); color:#fff; font-weight:700;
    font-size:14px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .apps-steps h3 { font-size:15px; margin:0 0 4px; color:var(--n-ink); display:flex; align-items:center; gap:6px; }
  .apps-steps h3 i { color:var(--pr); }
  .apps-steps p { font-size:13px; line-height:1.6; color:var(--n-mut); margin:0; }

  .apps-actions { display:flex; justify-content:center; gap:12px; flex-wrap:wrap; }
  .apps-cta, .apps-cta-secondary { display:inline-flex; align-items:center; gap:8px; border-radius:12px;
    font-size:14px; font-weight:600; text-decoration:none; transition:all .3s; }
  .apps-cta { background:var(--pr); color:#fff; padding:14px 28px; }
  .apps-cta:hover { background:var(--pr-lt); color:#fff; transform:translateY(-2px); }
  .apps-cta-secondary { color:var(--pr); border:2px solid var(--pr); padding:12px 26px; }
  .apps-cta-secondary:hover { background:var(--pr); color:#fff; }

  .apps-loader { min-height:60vh; display:flex; justify-content:center; align-items:center; }
  .apps-spinner { width:48px; height:48px; border:4px solid #e2e8f0; border-top-color:#004080; border-radius:50%; animation:apps-spin .8s linear infinite; }
  @keyframes apps-spin { to { transform:rotate(360deg); } }

  @media (max-width:768px) {
    .apps-hero { padding:100px 16px 32px; }
    .apps-title { font-size:30px; }
    .apps-audiences, .apps-steps { grid-template-columns:1fr; }
    .apps-steps-block { padding:22px 20px; }
  }
  @media (max-width:480px) {
    .apps-title { font-size:24px; }
    .apps-store { width:100%; }
    .apps-cta, .apps-cta-secondary { width:100%; justify-content:center; }
  }
`;